import { generateQuickstreamToken, ESupplierBusinessCode } from '@origin-digital/payments';
import { logger } from '@origin-digital/reporting-client';
import { ErrorFetch } from '@origin-digital/daxi';
import { IPaymentResponse, IAuthPayRequest } from '@origin-digital/payment-service-dapi-contract';

import { adhocPay, authPay, krakenPay, savedCardPay } from 'src/services';
import { IContext, IPaymentContext } from 'src/context/AppContext';
import { isMyAccount, isLpg, toCents, isKrakenId } from 'src/utils/helpers';

import { config } from 'src/config';

const isSavedCard = (context: IPaymentContext) => !!context.paymentMethod;

export const handleError = (error: ErrorFetch<any>, description: string): never => {
  logger.warn(description, error);

  if (error.fetchResponse && error.fetchResponse.data && error.fetchResponse.data.errorType) {
    throw Error(error.fetchResponse.data.errorType);
  }

  throw error;
};

export const makePaymentSaved = async ({
  amount,
  contractAccountId,
  paymentMethod,
}: IPaymentContext): Promise<IPaymentResponse> => {
  const paymentDetails: IAuthPayRequest = {
    contractAccountId,
    principalAmount: toCents(amount),
    eci: 'RECURRING',
    paymentCardId: paymentMethod.paymentCardId,
  };

  try {
    const { outcome, paymentResponse } = await savedCardPay(paymentDetails);

    if (!outcome.paymentSuccessful) {
      throw Error('Payment Unsuccessful');
    }

    return paymentResponse;
  } catch (error) {
    handleError(error, 'makePaymentSaved()');
  }
};

export const makePaymentAuthenticated = async (
  context: IPaymentContext,
): Promise<IPaymentResponse> => {
  const { amount, contractAccountId, paymentData, saveCard } = context;

  const singleUseTokenId = await generateQuickstreamToken(config.quickstreamKey, paymentData);

  const paymentDetails: IAuthPayRequest = {
    contractAccountId,
    singleUseTokenId,
    principalAmount: toCents(amount),
    saveCard,
  };

  try {
    const { outcome, paymentResponse } = await authPay({
      ...paymentDetails,
      eci: 'INTERNET',
    });

    if (!outcome.paymentSuccessful) {
      throw Error(outcome.responseCode);
    }

    if (saveCard && !outcome.cardSaved) {
      logger.warn('makePaymentAuthenticated(): Payment successful, card not saved due to error');
    }

    return paymentResponse;
  } catch (error) {
    handleError(error, 'makeQuickstreamPaymentAuthenticated()');
  }
};

export const makePaymentAdhoc = async (
  context: IPaymentContext,
  reCaptcha: string,
): Promise<IPaymentResponse> => {
  const { paymentData, contractAccountId, amount, divisionId, orderNumber } = context;

  const supplierBusinessCode = isLpg(divisionId)
    ? ESupplierBusinessCode.LPG
    : ESupplierBusinessCode.Retail;

  const singleUseTokenId = await generateQuickstreamToken(
    config.quickstreamKey,
    paymentData,
    supplierBusinessCode,
  );

  const paymentDetails: IAuthPayRequest = {
    contractAccountId,
    principalAmount: toCents(amount),
    singleUseTokenId,
    paymentReferenceNumber: orderNumber,
  };

  try {
    const { outcome, paymentResponse } = await adhocPay({
      reCaptcha,
      ...paymentDetails,
      supplierBusinessCode,
      eci: 'INTERNET',
    });

    if (!outcome.paymentSuccessful) {
      throw Error('Payment Unsuccessfull');
    }

    return paymentResponse;
  } catch (error) {
    handleError(error, 'makePaymentAdhoc()');
  }
};

export const makePaymentKraken = async (
  context: IPaymentContext,
  reCaptcha: string,
): Promise<IPaymentResponse> => {
  const { paymentData, contractAccountId, amount: dollarAmount } = context;

  const singleUseTokenId = await generateQuickstreamToken(
    config.quickstreamKey,
    paymentData,
    ESupplierBusinessCode.RetailX,
  );

  try {
    const { amount, payment_date: timestamp, reference: receiptNumber } = await krakenPay(
      {
        amount: toCents(dollarAmount),
        single_use_token: singleUseTokenId,
        captcha_token: reCaptcha,
        electronic_commerce_indicator: 'INTERNET',
        supplier_business_code: ESupplierBusinessCode.RetailX,
      },
      contractAccountId,
    );

    return {
      receiptNumber,
      timestamp,
      contractAccountId,
      amount,
    };
  } catch (e) {
    const error = e as ErrorFetch;
    if (error.fetchResponse && error.fetchResponse.status === 404) {
      throw Error('invalid-kraken-account');
    } else {
      handleError(error, 'makePaymentKraken()');
    }
  }
};

export const makePayment = async (
  context: IContext,
  recaptchaToken: string,
  /* istanbul ignore next */ {
    makePaymentAdhoc: __makePaymentAdhoc = makePaymentAdhoc,
    makePaymentSaved: __makePaymentSaved = makePaymentSaved,
    makePaymentAuthenticated: __makePaymentAuthenticated = makePaymentAuthenticated,
    makePaymentKraken: __makePaymentKraken = makePaymentKraken,
  } = {},
): Promise<IPaymentResponse> => {
  const paymentContext = {
    ...context,
    amount: parseFloat(context.amount.replace(/,/g, '')),
  };

  if (isKrakenId(context.contractAccountId)) {
    return await __makePaymentKraken(paymentContext, recaptchaToken);
  }

  if (isMyAccount()) {
    if (isSavedCard(paymentContext)) {
      return await __makePaymentSaved(paymentContext);
    }

    return await __makePaymentAuthenticated(paymentContext);
  }

  return await __makePaymentAdhoc(paymentContext, recaptchaToken);
};
